// Collection product card: colour swatches select (preview on hover, lock on click/tap)
// the displayed colour. Swatches never navigate — only the main image / title link do.
class ProductGridItem extends HTMLElement {
  constructor() {
    super();

    this.nav = this.querySelector(".carousel__nav");
    if (!this.nav) return; // single-colour card or legacy single-image card: nothing to wire up

    this.mainImage = this.querySelector(".card__main-image");
    this.mainLink = this.querySelector(".card__main-link");
    this.titleLink = this.querySelector(".featured-products-grid_item_title a");
    this.priceBlock = this.querySelector(".product-info__price-block");
    this.swatches = Array.from(this.nav.querySelectorAll(".card-swatch"));
    if (!this.mainImage || this.swatches.length === 0) return;

    // The server marks the lead colour's swatch .current-slide and renders the matching
    // image/price/links. Snapshot that resting state so hover-preview can be undone.
    this.locked = this.nav.querySelector(".card-swatch.current-slide");
    const restBadge = this.mainLink ? this.mainLink.querySelector(".product-sale-badge") : null;
    this.rest = {
      src: this.mainImage.getAttribute("src"),
      srcset: this.mainImage.getAttribute("srcset"),
      alt: this.mainImage.getAttribute("alt"),
      price: this.priceBlock ? this.priceBlock.innerHTML : null,
      badge: restBadge ? restBadge.outerHTML : "",
    };

    // Warm the full-size colour images on first intent so swatch swaps are instant.
    this.warmed = false;
    const warm = () => {
      if (this.warmed) return;
      this.warmed = true;
      this.swatches.forEach((s) => {
        const url = s.dataset.fullImage;
        if (url) {
          const img = new Image();
          img.src = url;
        }
      });
    };
    this.addEventListener("mouseenter", warm, { once: true });
    this.addEventListener("touchstart", warm, { once: true, passive: true });

    this.nav.addEventListener("click", (e) => {
      const swatch = e.target.closest(".card-swatch");
      if (swatch) this.lockSelection(swatch);
    });

    this.nav.addEventListener("mouseover", (e) => {
      const swatch = e.target.closest(".card-swatch");
      if (swatch) this.applyVisual(swatch);
    });

    this.nav.addEventListener("mouseleave", () => {
      if (this.locked) {
        this.applyVisual(this.locked);
      } else {
        this.restoreRest();
      }
    });
  }

  // Swap the main image + price to a swatch's colour (the transient preview).
  applyVisual = (swatch) => {
    const img = swatch.dataset.fullImage;
    if (img) {
      this.mainImage.removeAttribute("srcset");
      this.mainImage.src = img;
      const color = swatch.getAttribute("aria-label") || "";
      const baseAlt = (this.rest.alt || "").split(" – ")[0];
      this.mainImage.alt = color ? baseAlt + " – " + color : this.rest.alt || "";
    }
    if (this.priceBlock && swatch.dataset.priceHtml) {
      this.priceBlock.innerHTML = swatch.dataset.priceHtml;
    }
    // Keep the sale badge in step with the displayed colour (empty string => no badge).
    if ("badgeHtml" in swatch.dataset) {
      this.setBadge(swatch.dataset.badgeHtml);
    }
  };

  // Swap the sale badge inside the main-link. `html` is a full
  // <div class="product-sale-badge">…</div> string, or "" for no badge.
  setBadge = (html) => {
    if (!this.mainLink) return;
    const current = this.mainLink.querySelector(".product-sale-badge");
    if (html) {
      if (current) {
        current.outerHTML = html;
      } else {
        this.mainLink.insertAdjacentHTML("afterbegin", html);
      }
    } else if (current) {
      current.remove();
    }
  };

  // Restore the resting state captured at load (used when nothing is locked).
  restoreRest = () => {
    if (this.rest.srcset) {
      this.mainImage.setAttribute("srcset", this.rest.srcset);
    } else {
      this.mainImage.removeAttribute("srcset");
    }
    if (this.rest.src) this.mainImage.setAttribute("src", this.rest.src);
    if (this.rest.alt != null) this.mainImage.alt = this.rest.alt;
    if (this.priceBlock && this.rest.price != null) {
      this.priceBlock.innerHTML = this.rest.price;
    }
    this.setBadge(this.rest.badge);
  };

  // Lock a colour: preview it, re-point the image/title links to its variant, mark it active.
  lockSelection = (swatch) => {
    this.applyVisual(swatch);
    this.locked = swatch;

    const url = swatch.dataset.variantUrl;
    if (url) {
      if (this.mainLink) this.mainLink.href = url;
      if (this.titleLink) this.titleLink.href = url;
    }

    this.swatches.forEach((s) => {
      const on = s === swatch;
      s.classList.toggle("current-slide", on);
      s.setAttribute("aria-pressed", on ? "true" : "false");
    });
  };
}

customElements.define("product-grid-item", ProductGridItem);
